import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "../utils/api";
import { motion } from "framer-motion";
import { FaUsers, FaFolder, FaCalendarAlt, FaMoneyBillWave } from "react-icons/fa";

/* ===========================================================
  Helpers
=========================================================== */
const isIOS = () =>
  /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

const isStandalone = () =>
  window.matchMedia?.("(display-mode: standalone)")?.matches ||
  window.navigator?.standalone === true;

function Dashboard() {
  const [stats, setStats] = useState({
    totalClients: 0,
    totalCases: 0,
    totalMeetings: 0,
    totalRevenue: 0,
  });

  const [revenueCount, setRevenueCount] = useState(0);

  // PWA install
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallBtn, setShowInstallBtn] = useState(false);
  const [showIOSHint, setShowIOSHint] = useState(false);

  const revenueTimerRef = useRef(null);

  /* ===========================================================
    ✅ PWA Install Handling (Android/Chrome + iOS hint)
  =========================================================== */
  useEffect(() => {
    // لو التطبيق متثبت بالفعل → مخبي الزر
    if (isStandalone()) {
      setShowInstallBtn(false);
      setShowIOSHint(false);
      return;
    }

    // iOS: مفيش beforeinstallprompt
    if (isIOS()) {
      setShowIOSHint(true);
    }

    const handler = (e) => {
      // Chrome/Edge/Android: قبل التثبيت
      e.preventDefault();
      setDeferredPrompt(e);
      setShowInstallBtn(true);
    };

    window.addEventListener("beforeinstallprompt", handler);

    // لما المستخدم يثبت التطبيق فعلاً
    const onAppInstalled = () => {
      setDeferredPrompt(null);
      setShowInstallBtn(false);
      setShowIOSHint(false);
    };

    window.addEventListener("appinstalled", onAppInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      window.removeEventListener("appinstalled", onAppInstalled);
    };
  }, []);

  const handleInstallClick = async () => {
    try {
      // Android/Chrome
      if (deferredPrompt) {
        deferredPrompt.prompt();
        const choice = await deferredPrompt.userChoice;
        // سواء قبل أو رفض: امسح الـ prompt (ده الطبيعي)
        setDeferredPrompt(null);

        // لو قبل التثبيت
        if (choice?.outcome === "accepted") {
          setShowInstallBtn(false);
          setShowIOSHint(false);
        }
        return;
      }

      // iOS: هنعرض تلميح
      if (isIOS()) {
        setShowIOSHint(true);
        return;
      }

      console.warn("ℹ️ المتصفح لا يدعم نافذة التثبيت حاليًا.");
    } catch (err) {
      console.error("❌ خطأ أثناء محاولة التثبيت:", err);
    }
  };

  /* ===========================================================
    ✅ Fetch Stats + Smooth Revenue Counter
  =========================================================== */
  const fetchStats = async () => {
    try {
      const res = await api.get("/dashboard");
      const data = res.data || {};

      const nextStats = {
        totalClients: Number(data.totalClients || 0),
        totalCases: Number(data.totalCases || 0),
        totalMeetings: Number(data.totalMeetings || 0),
        totalRevenue: Number(data.totalRevenue || 0),
      };

      setStats(nextStats);

      // Clean previous timer
      if (revenueTimerRef.current) clearInterval(revenueTimerRef.current);

      // Animate revenue
      const end = nextStats.totalRevenue;
      const durationMs = 1200;
      const stepMs = 30;
      const steps = Math.max(1, Math.floor(durationMs / stepMs));
      const increment = end / steps;

      let current = 0;
      setRevenueCount(0);

      revenueTimerRef.current = setInterval(() => {
        current += increment;
        if (current >= end) {
          clearInterval(revenueTimerRef.current);
          revenueTimerRef.current = null;
          setRevenueCount(end);
        } else {
          setRevenueCount(Math.floor(current));
        }
      }, stepMs);
    } catch (err) {
      console.error("❌ فشل تحميل الإحصائيات", err);
    }
  };

  useEffect(() => {
    fetchStats();
    return () => {
      if (revenueTimerRef.current) clearInterval(revenueTimerRef.current);
    };
  }, []);

  /* ===========================================================
    Animations
  =========================================================== */
  const cardVariants = useMemo(
    () => ({
      hidden: { opacity: 0, scale: 0.92, y: 18 },
      visible: { opacity: 1, scale: 1, y: 0 },
    }),
    []
  );

  return (
    <div className="relative p-8 bg-gradient-to-br from-gray-900 via-black to-gray-800 min-h-screen text-white">
      {/* ✅ زر تثبيت (Android/Chrome) */}
      {showInstallBtn && (
        <motion.button
          initial={{ opacity: 0, y: -16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
          onClick={handleInstallClick}
          className="fixed top-6 left-6 z-50 bg-gradient-to-br from-red-500 to-red-700 text-white font-bold py-3 px-6 rounded-full shadow-lg hover:scale-105 active:scale-[.98] transition-all flex items-center gap-2"
          aria-label="تثبيت تطبيق الأفوكاتو"
          title="تثبيت تطبيق الأفوكاتو"
        >
          📱 تنزيل الأفوكاتو
        </motion.button>
      )}

      {/* ✅ iOS Hint */}
      {showIOSHint && !showInstallBtn && !isStandalone() && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
          className="fixed top-6 left-6 z-50 max-w-[320px] bg-white text-gray-900 rounded-2xl shadow-xl border border-gray-200 p-4"
          role="status"
          aria-live="polite"
        >
          <div className="font-extrabold text-sm mb-1">📱 تثبيت الأفوكاتو على iPhone</div>
          <div className="text-xs text-gray-700 leading-5">
            افتح القائمة <b>Share</b> ثم اختر <b>Add to Home Screen</b>.
          </div>

          <button
            onClick={() => setShowIOSHint(false)}
            className="mt-3 w-full bg-gray-900 text-white py-2 rounded-xl font-bold hover:bg-black transition"
          >
            تمام
          </button>
        </motion.div>
      )}

      <motion.h1
        className="text-4xl font-bold text-yellow-400 mb-10 text-center"
        initial={{ opacity: 0, y: -24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.65, ease: "easeOut" }}
      >
        👑 لوحة التحكم
      </motion.h1>

      <motion.div
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
        initial="hidden"
        animate="visible"
        variants={{ visible: { transition: { staggerChildren: 0.14 } } }}
      >
        {/* العملاء */}
        <motion.div
          className="bg-gray-900 border-2 border-yellow-500 rounded-2xl shadow-2xl p-6 flex flex-col items-center text-center hover:scale-105 transition"
          variants={cardVariants}
        >
          <FaUsers className="text-yellow-400 text-5xl mb-4 animate-bounce" />
          <h2 className="text-xl font-semibold mb-2">العملاء</h2>
          <p className="text-3xl font-bold">{stats.totalClients}</p>
        </motion.div>

        {/* القضايا */}
        <motion.div
          className="bg-gray-900 border-2 border-yellow-500 rounded-2xl shadow-2xl p-6 flex flex-col items-center text-center hover:scale-105 transition"
          variants={cardVariants}
        >
          <FaFolder className="text-yellow-400 text-5xl mb-4 animate-pulse" />
          <h2 className="text-xl font-semibold mb-2">القضايا</h2>
          <p className="text-3xl font-bold">{stats.totalCases}</p>
        </motion.div>

        {/* المواعيد */}
        <motion.div
          className="bg-gray-900 border-2 border-yellow-500 rounded-2xl shadow-2xl p-6 flex flex-col items-center text-center hover:scale-105 transition"
          variants={cardVariants}
        >
          <FaCalendarAlt className="text-yellow-400 text-5xl mb-4 animate-spin-slow" />
          <h2 className="text-xl font-semibold mb-2">المواعيد</h2>
          <p className="text-3xl font-bold">{stats.totalMeetings}</p>
        </motion.div>

        {/* الإيرادات */}
        <motion.div
          className="bg-gradient-to-r from-yellow-500 to-yellow-300 text-black rounded-2xl shadow-2xl p-6 flex flex-col items-center text-center hover:scale-105 transition"
          variants={cardVariants}
        >
          <FaMoneyBillWave className="text-black text-5xl mb-4 animate-wiggle" />
          <h2 className="text-xl font-semibold mb-2">إجمالي الإيرادات</h2>
          <p className="text-3xl font-bold">{revenueCount} 💰</p>
        </motion.div>
      </motion.div>
    </div>
  );
}

export default Dashboard;
